import { ReactNode, useMemo, useState } from "react";
import { getAffectedClientWorkspaceIds } from "@common/helpers/organizationMemberClientAccess";
import { AuditLogChangeType, HistoryDetailsItem } from "@common/types/auditLog";
import { View } from "@common/types/views/views";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import historyDetailsStyles from "@components/history/details/HistoryDetails.module.scss";
import {
    buildOrganizationActivityDetailItems,
    getOrganizationActivityRoleLabel,
} from "../describeOrganizationActivity";
import { useOrganizationClients } from "../useOrganizationClients";
import {
    createDefaultOrganizationActivityUpdateDetailsView,
    createOrganizationActivityCreateDeleteViewConfig,
    createOrganizationActivityUpdateViewConfig,
    defaultOrganizationActivityCreateDeleteDetailsView,
    OrganizationActivityCreateDeleteDetailsRow,
    OrganizationActivityUpdateDetailsRow,
} from "./organizationActivityDetailsViewConfig";

interface OrganizationMemberActivityDetailsProps {
    logs: HistoryDetailsItem[];
    footer: ReactNode;
}

const renderEmptyView = () => <EmptyState header="No member changes found" />;

export const OrganizationMemberCreateDeleteDetails = ({
    logs,
    footer,
}: OrganizationMemberActivityDetailsProps) => {
    const [view, setView] = useState<View>(
        defaultOrganizationActivityCreateDeleteDetailsView,
    );
    const config = useMemo(
        () => createOrganizationActivityCreateDeleteViewConfig(),
        [],
    );

    const data = useMemo<OrganizationActivityCreateDeleteDetailsRow[]>(
        () =>
            logs
                .filter(
                    ({ changeType }) =>
                        changeType === AuditLogChangeType.CREATE ||
                        changeType === AuditLogChangeType.DELETE,
                )
                .map((log) => ({
                    id: log.id,
                    date: log.createdAt,
                    logDate: log.createdAt,
                    entityId: log.entityId,
                    resourceType: log.resourceType,
                    resourceId: log.resourceId,
                    diff: log.diff,
                    clientWorkspaceIds: getAffectedClientWorkspaceIds(log.diff),
                    roleLabel: getOrganizationActivityRoleLabel(log.diff),
                })),
        [logs],
    );

    return (
        <ViewTable
            data={data}
            view={view}
            config={config}
            onViewChange={setView}
            emptyView={renderEmptyView}
            footer={footer}
            className={historyDetailsStyles.historyDetailsView}
        />
    );
};

export const OrganizationMemberUpdateDetails = ({
    logs,
    footer,
}: OrganizationMemberActivityDetailsProps) => {
    const { data: workspacesList } = useOrganizationClients();
    const clientNamesById = useMemo(
        () =>
            new Map(
                (workspacesList ?? []).map((workspace) => [
                    workspace.id,
                    workspace.name,
                ]),
            ),
        [workspacesList],
    );

    const data = useMemo<OrganizationActivityUpdateDetailsRow[]>(
        () =>
            logs
                .filter(
                    ({ changeType }) =>
                        changeType === AuditLogChangeType.UPDATE,
                )
                .flatMap((log) =>
                    buildOrganizationActivityDetailItems({
                        diff: log.diff,
                        clientNamesById,
                    }).map((item) => ({
                        id: `${log.id}:${item.id}`,
                        date: log.createdAt,
                        logDate: log.createdAt,
                        entityId: log.entityId,
                        resourceType: log.resourceType,
                        resourceId: log.resourceId,
                        diff: log.diff,
                        clientWorkspaceIds: getAffectedClientWorkspaceIds(
                            log.diff,
                        ),
                        fieldLabel: item.fieldLabel,
                        previous: item.previous,
                        modified: item.modified,
                    })),
                ),
        [logs, clientNamesById],
    );

    const hasMultipleFields =
        new Set(data.map(({ fieldLabel }) => fieldLabel)).size > 1;
    const [view, setView] = useState<View>(() =>
        createDefaultOrganizationActivityUpdateDetailsView(hasMultipleFields),
    );
    const [prevHasMultipleFields, setPrevHasMultipleFields] =
        useState(hasMultipleFields);
    if (hasMultipleFields !== prevHasMultipleFields) {
        setPrevHasMultipleFields(hasMultipleFields);
        setView(
            createDefaultOrganizationActivityUpdateDetailsView(
                hasMultipleFields,
            ),
        );
    }

    const config = useMemo(
        () => createOrganizationActivityUpdateViewConfig(data),
        [data],
    );

    return (
        <ViewTable
            data={data}
            view={view}
            config={config}
            onViewChange={setView}
            emptyView={renderEmptyView}
            footer={footer}
            className={historyDetailsStyles.historyDetailsView}
        />
    );
};
